'use client'

import { useRouter } from 'next/router'
import { useTheme } from 'styled-components'
import { useMemo } from 'react'

import { AppTheme } from 'styled-components'

/**
 * Hook to get theme key with fallback priority:
 * 1. prop (if provided)
 * 2. pathname detection (/sunpoint or /wellmaxx)
 * 3. styled-components ThemeProvider (for backward compatibility)
 */
export function useThemeKey(
  themeKeyProp?: 'sunpoint' | 'wellmaxx'
): 'sunpoint' | 'wellmaxx' {
  const router = useRouter()
  const pathname = router.asPath
  
  // Call useTheme() unconditionally (React Hook rule)
  // The app always has ThemeProvider, so this is safe
  // If ThemeProvider is missing, it will throw, but that's expected
  const theme = useTheme() as AppTheme | undefined
  const themeFromContext = theme

  return useMemo(() => {
    // Priority 1: Explicit prop
    if (themeKeyProp) {
      return themeKeyProp
    }

    // Priority 2: Detect from pathname
    if (pathname) {
      if (pathname.startsWith('/sunpoint')) {
        return 'sunpoint'
      }
      if (pathname.startsWith('/wellmaxx')) {
        return 'wellmaxx'
      }
    }

    // Priority 3: Fallback to styled-components theme context
    if (themeFromContext?.key) {
      return themeFromContext.key as 'sunpoint' | 'wellmaxx'
    }

    // Final fallback: default to sunpoint
    return 'sunpoint'
  }, [themeKeyProp, pathname, themeFromContext])
}
